import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/hooks/useAuth';
import { normalizeCurrency } from '@/lib/currency';
import ShopPaymentModal from '@/components/feature/ShopPaymentModal';
import {
  createFormationOrder,
  fetchFormationContent,
  fetchPublicFormation,
  formationPrice,
  grantFormationAccess,
  hasFormationAccess,
  youtubeEmbedUrl,
  type FormationChapter,
  type FormationFile,
  type FormationLesson,
  type PublicFormation,
} from '@/lib/publicFormations';

export default function DetailFormationPage() {
  const { id } = useParams<{ id: string }>();
  const formationId = Number(id);
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const currency = normalizeCurrency(tenant?.monaie || 'MAD');

  const [formation, setFormation] = useState<PublicFormation | null>(null);
  const [chapters, setChapters] = useState<FormationChapter[]>([]);
  const [lessons, setLessons] = useState<FormationLesson[]>([]);
  const [files, setFiles] = useState<FormationFile[]>([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [payOrder, setPayOrder] = useState<{ id: number; total: number } | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(formationId)) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const f = await fetchPublicFormation(formationId);
      if (!f) { setNotFound(true); setLoading(false); return; }
      setFormation(f);

      const content = await fetchFormationContent(f.id);
      setChapters(content.chapters);
      setLessons(content.lessons);
      setFiles(content.files);

      if (user && tenant) {
        const access = await hasFormationAccess(user.id, f.id);
        setHasAccess(access);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger la formation.');
    } finally {
      setLoading(false);
    }
  }, [formationId, user, tenant]);

  useEffect(() => { void load(); }, [load]);

  const handleEnroll = async () => {
    if (!formation) return;
    if (!user) {
      navigate(`/register-client?redirect=/formation/${formation.id}`);
      return;
    }
    // Le formateur est le commerce du thème ; sur le site principal on
    // retombe sur le formateur enregistré dans la formation.
    const merchantId = tenant?.id || formation.id_formateur || 0;
    const price = formationPrice(formation);
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (price <= 0) {
        if (!merchantId) throw new Error('Formateur introuvable pour cette formation.');
        await grantFormationAccess({ formationId: formation.id, merchantId, userId: user.id });
        setHasAccess(true);
        setMessage('Inscription confirmée ! Votre formation est disponible ci-dessous.');
        return;
      }

      // Formation payante : même tunnel de paiement que le checkout boutique.
      const orderId = await createFormationOrder({
        formation,
        merchantId,
        userId: user.id,
        buyerName: user.name || user.user_name || 'Client',
        buyerEmail: user.email || '',
        buyerPhone: user.telephone || '',
        currency,
        amount: price,
      });
      setPayOrder({ id: orderId, total: price });
    } catch (err) {
      setError(err instanceof Error ? err.message : "L'inscription n'a pas pu aboutir.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (notFound || !formation) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Formation introuvable</h1>
        <p className="text-sm text-foreground-500 mb-6 max-w-md">
          Cette formation n&apos;existe plus ou n&apos;est pas encore publiée.
        </p>
        <Link to="/formations" className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline whitespace-nowrap">
          Voir toutes les formations
        </Link>
      </div>
    );
  }

  const price = formationPrice(formation);
  const embed = youtubeEmbedUrl(formation.video_url);

  return (
    <div className="min-h-screen bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-6xl mx-auto pt-6">
        <Link
          to="/formations"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors no-underline mb-4"
        >
          <i className="ri-arrow-left-line"></i>Toutes les formations
        </Link>
      </div>

      <section className="w-full px-4 md:px-6 max-w-6xl mx-auto pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl overflow-hidden bg-background-100 border border-background-200/70">
              <div className="relative h-56 md:h-80">
                {formation.image ? (
                  <img
                    src={formation.image}
                    alt={formation.titre}
                    title={`${formation.titre} — formation`}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-secondary-50">
                    <i className="ri-play-circle-line text-6xl text-primary-300"></i>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {formation.categorie && (
                  <span className="px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-700 text-[11px] font-semibold">
                    {formation.categorie}
                  </span>
                )}
                {formation.souscategorie && (
                  <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[11px] font-semibold">
                    {formation.souscategorie}
                  </span>
                )}
                {formation.duree && (
                  <span className="text-[11px] text-foreground-500 flex items-center gap-1">
                    <i className="ri-time-line"></i>{formation.duree}
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight">
                {formation.titre}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-3 whitespace-pre-line">
                {formation.description_long || formation.description || 'Programme détaillé à venir.'}
              </p>
            </div>

            {embed && hasAccess && (
              <div className="aspect-video rounded-xl overflow-hidden bg-foreground-950">
                <iframe
                  src={embed}
                  title={formation.titre}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            {/* Programme */}
            <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200/70">
                <h2 className="text-sm font-bold font-heading text-foreground-950 flex items-center gap-2">
                  <i className="ri-list-check-2 text-primary-500"></i>
                  Programme de la formation
                </h2>
              </div>
              {lessons.length === 0 && chapters.length === 0 ? (
                <p className="px-5 py-8 text-sm text-foreground-500 text-center">
                  Le programme détaillé sera publié prochainement.
                </p>
              ) : (
                <div className="divide-y divide-background-200/60">
                  {chapters.length > 0 && chapters.map((ch) => (
                    <div key={`ch-${ch.id}`} className="px-5 py-3.5">
                      <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                        <i className="ri-folder-open-line text-accent-500"></i>
                        {ch.titre || 'Chapitre'}
                      </h3>
                      {ch.description && <p className="text-xs text-foreground-500 mt-1">{ch.description}</p>}
                    </div>
                  ))}
                  {lessons.map((lesson, index) => (
                    <div key={`ls-${lesson.id}`} className="px-5 py-3 flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                        hasAccess ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'
                      }`}>
                        {hasAccess ? <i className="ri-play-fill"></i> : index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-900 truncate">
                          {lesson.titre || `Leçon ${index + 1}`}
                        </p>
                        {lesson.duree && <p className="text-[11px] text-foreground-400">{lesson.duree}</p>}
                      </div>
                      {!hasAccess && <i className="ri-lock-line text-foreground-300 text-sm"></i>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Ressources (si accès) */}
            {hasAccess && (files.length > 0 || formation.pdf_url) && (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <h2 className="text-sm font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-attachment-2 text-primary-500"></i>
                  Supports de la formation
                </h2>
                <div className="space-y-2">
                  {formation.pdf_url && (
                    <a
                      href={formation.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors no-underline"
                    >
                      <i className="ri-file-pdf-line text-red-500"></i>
                      <span className="text-sm text-foreground-800 flex-1 truncate">Support PDF</span>
                      <i className="ri-download-2-line text-foreground-500"></i>
                    </a>
                  )}
                  {files.map((file) => (
                    <a
                      key={file.id}
                      href={file.fichier || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors no-underline"
                    >
                      <i className="ri-file-line text-primary-500"></i>
                      <span className="text-sm text-foreground-800 flex-1 truncate">{file.titre || 'Ressource'}</span>
                      <i className="ri-download-2-line text-foreground-500"></i>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Colonne inscription */}
          <aside className="lg:col-span-1">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 sticky top-20">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground-400">Tarif</p>
              <p className="text-3xl font-bold font-heading text-foreground-950 mt-1">
                {price > 0 ? `${price.toLocaleString('fr-FR')} ${currency}` : 'Gratuit'}
              </p>

              {message && (
                <div className="mt-3 px-3 py-2.5 rounded-lg bg-accent-50 border border-accent-200/70 text-xs text-accent-800 flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5"></i>
                  <span>{message}</span>
                </div>
              )}
              {error && (
                <div className="mt-3 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <span>{error}</span>
                </div>
              )}

              {hasAccess ? (
                <div className="mt-4 space-y-3">
                  <div className="px-3 py-2.5 rounded-lg bg-accent-100 text-accent-800 text-xs font-semibold flex items-center gap-2">
                    <i className="ri-shield-check-line"></i>
                    Vous avez accès à cette formation
                  </div>
                  <Link
                    to="/mon-compte/courses"
                    className="w-full flex items-center justify-center gap-2 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-graduation-cap-line"></i>Ouvrir mes formations
                  </Link>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  <button
                    type="button"
                    onClick={() => void handleEnroll()}
                    disabled={busy}
                    className="w-full h-12 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-user-add-line"></i>}
                    {price > 0 ? 'Payer et m\u2019inscrire' : 'M\u2019inscrire gratuitement'}
                  </button>
                  {!user && (
                    <p className="text-[11px] text-foreground-500 leading-relaxed text-center">
                      Un compte est nécessaire pour conserver votre accès.{' '}
                      <Link to={`/login-client?redirect=/formation/${formation.id}`} className="text-primary-600 font-semibold no-underline">
                        Se connecter
                      </Link>
                    </p>
                  )}
                  {price > 0 && (
                    <p className="flex items-center justify-center gap-1.5 text-[11px] text-foreground-400">
                      <i className="ri-shield-check-line"></i>
                      Paiement géré par notre partenaire FeexPay
                    </p>
                  )}
                </div>
              )}

              <ul className="mt-5 pt-4 border-t border-background-200/70 space-y-2 text-xs text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  Accès immédiat après inscription
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  Retrouvez la formation dans votre compte
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  Leçons, vidéos et supports téléchargeables
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {payOrder && formation && (
        <ShopPaymentModal
          orderId={payOrder.id}
          total={payOrder.total}
          currency={currency}
          buyerName={user?.name || ''}
          buyerEmail={user?.email || ''}
          buyerPhone={user?.telephone || ''}
          merchantName={tenant?.nomcommerce || 'le formateur'}
          onClose={() => setPayOrder(null)}
          onPaid={() => {
            void (async () => {
              if (user) {
                const merchantId = tenant?.id || formation.id_formateur || 0;
                if (merchantId) {
                  await grantFormationAccess({ formationId: formation.id, merchantId, userId: user.id });
                }
                setHasAccess(true);
                setMessage('Paiement confirmé ! Votre accès à la formation est ouvert.');
              }
              setPayOrder(null);
            })();
          }}
        />
      )}
    </div>
  );
}